/**
 * PDF Export Service for Annual Planner
 *
 * This service fetches data directly from the API and renders it
 * in a dedicated export container for clean PDF generation.
 */

import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { axiosAuthed } from '../../../shared/utils/setCommonHeaders';
import {
  getWeeksInRange,
  getWeekRangeDates,
  formatWeekDateRangeCompact,
} from '../utils/weekUtils';
import {
  hexToColorId,
  getColorOptionForBlock,
} from '../components/blockColors';
import TeamBuildrLogo from '../../../shared/images/t-logo-xl.png';

/**
 * Escape HTML special characters to prevent XSS
 */
const escapeHtml = (str) => {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/\//g, '&#x2F;');
};

// PDF Configuration (A4 portrait)
const PDF_CONFIG = {
  pageWidth: 210, // mm
  pageHeight: 297, // mm
  marginTop: 8, // mm
  marginBottom: 8, // mm
  marginLeft: 8, // mm
  marginRight: 8, // mm
  canvasScale: 2,
};

// S3 bucket URLs for direct image access (avoids CORS issues with presigned URLs)
const S3_BUCKET_URLS = {
  staging: 'https://teambuildr-staging-assets.s3.us-east-1.amazonaws.com',
  production: 'https://teambuildr-production-assets.s3.us-east-1.amazonaws.com',
};

/**
 * Convert org logo URL to direct S3 bucket URL format
 */
const getOrgLogoDirectUrl = (logoInput) => {
  if (!logoInput) return null;

  const isProduction = process.env.ENVIRONMENT === 'PRODUCTION';
  const bucketUrl = isProduction ? S3_BUCKET_URLS.production : S3_BUCKET_URLS.staging;

  // Extract filename from full URL
  let filename = logoInput;

  if (logoInput.includes('/logos/')) {
    const parts = logoInput.split('/logos/');
    filename = parts[parts.length - 1];
  }

  return `${bucketUrl}/logos/${filename}`;
};

/**
 * Preload an image and return as base64 data URL
 */
const preloadImage = (src) => new Promise((resolve) => {
  if (!src) {
    resolve(null);
    return;
  }

  const img = new Image();
  img.crossOrigin = 'anonymous';
  img.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    } catch (e) {
      // CORS or other error, use original src
      resolve(src);
    }
  };
  img.onerror = () => resolve(null);
  img.src = src;
});

/**
 * Fetch plan data directly from API for the export range
 */
const fetchExportData = async ({
  accountCode, planId, fromWeek, toWeek,
}) => {
  const { startDate, endDate } = getWeekRangeDates(fromWeek, toWeek);

  const response = await axiosAuthed.get(
    `/accounts/${accountCode}/plans/${planId}?startDate=${startDate}&endDate=${endDate}`,
    { timeout: 30000 },
  );

  return response.data;
};

/**
 * Process API data into week-based structure
 */
const processDataForExport = (apiData, fromWeek, toWeek) => {
  const weeks = getWeeksInRange(fromWeek, toWeek);
  const { planBlocks = [], planNotes = [] } = apiData;

  // Convert blocks to frontend format and group by week
  const blocksByWeek = {};
  planBlocks.forEach((block) => {
    if (block.del) return;

    const colorId = hexToColorId(block.color);
    const blockData = {
      id: String(block.id),
      name: block.name,
      colorId,
      startDate: block.startDate,
      endDate: block.endDate,
    };

    weeks.forEach((week) => {
      const { startDate: weekStart, endDate: weekEnd } = getWeekRangeDates(week, week);
      if (block.startDate <= weekEnd && block.endDate >= weekStart) {
        if (!blocksByWeek[week]) {
          blocksByWeek[week] = [];
        }
        if (!blocksByWeek[week].find((b) => b.id === blockData.id)) {
          blocksByWeek[week].push(blockData);
        }
      }
    });
  });

  // Convert notes to week-based map
  const notesByWeek = {};
  planNotes.forEach((note) => {
    if (note.del) return;

    const noteDate = note.date;
    weeks.forEach((week) => {
      const { startDate: weekStart } = getWeekRangeDates(week, week);
      if (noteDate === weekStart) {
        notesByWeek[week] = note.note;
      }
    });
  });

  return { weeks, blocksByWeek, notesByWeek };
};

/**
 * Get color styles for a block
 */
const getBlockStyles = (colorId) => {
  const colorOption = getColorOptionForBlock({ colorId });

  return {
    background: colorOption.background,
    border: colorOption.border,
    text: colorOption.text || '#444444',
  };
};

/**
 * Create the export container with rendered content
 */
const createExportContainer = ({
  weeks,
  blocksByWeek,
  notesByWeek,
  calendarName,
  orgLogoUrl,
  teamBuildrLogoBase64,
  isFirstPage,
  allWeeks,
  globalWeekStartIndex,
}) => {
  const container = document.createElement('div');
  container.style.cssText = `
    position: absolute;
    left: -9999px;
    top: 0;
    width: 1000px;
    background: white;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 12px;
    color: #333;
  `;

  let html = '';

  // Add PDF header only on first page
  if (isFirstPage) {
    let orgLogoHtml = '';
    if (orgLogoUrl) {
      orgLogoHtml = `<img src="${orgLogoUrl}" alt="Logo" crossorigin="anonymous" style="max-height: 36px; max-width: 100px; object-fit: contain;" />`;
    }

    html += `
      <div style="display: flex; justify-content: space-between; align-items: center; padding: 12px 16px; border-bottom: 1px solid #e0e0e0; margin-bottom: 4px;">
        <div style="flex: 1; min-width: 100px;">
          ${orgLogoHtml}
        </div>
        <div style="flex: 2; text-align: center;">
          <h2 style="margin: 0; font-size: 16px; font-weight: 600; color: #333;">${escapeHtml(calendarName) || 'Training Plan'}</h2>
        </div>
        <div style="flex: 1; text-align: right; min-width: 100px;">
          ${teamBuildrLogoBase64 ? `<img src="${teamBuildrLogoBase64}" alt="TeamBuildr" style="height: 28px; object-fit: contain;" />` : '<span style="font-size: 11px; color: #666;">TeamBuildr</span>'}
        </div>
      </div>
    `;
  }

  // Add table header
  html += `
    <div style="display: flex; background: #ffffff; border-bottom: 2px solid #e0e0e0; padding: 8px 0; font-weight: 600;">
      <div style="width: 130px; padding: 0 10px;">Week</div>
      <div style="flex: 1; padding: 0 10px;">Blocks</div>
      <div style="width: 260px; padding: 0 10px;">Notes</div>
    </div>
  `;

  // Track previous year for year change detection
  let previousYear = null;

  // Add rows for each week
  weeks.forEach((week, localIndex) => {
    const weekMatch = week.match(/^(\d{4})-W(\d{2})$/);
    const weekYear = weekMatch ? parseInt(weekMatch[1], 10) : null;
    const weekNumber = weekMatch ? parseInt(weekMatch[2], 10) : '';
    const dateRange = formatWeekDateRangeCompact(week);
    const blocks = blocksByWeek[week] || [];
    const noteText = notesByWeek[week] || '';

    // Determine if we need to show year indicator
    // Show on first week of a new year OR when year changes from previous week
    const globalIndex = globalWeekStartIndex + localIndex;
    let prevWeekYear = previousYear;

    if (globalIndex > 0 && localIndex === 0 && !isFirstPage) {
      // First row of non-first page - check the previous week from allWeeks
      const prevWeek = allWeeks[globalIndex - 1];
      const prevMatch = prevWeek?.match(/^(\d{4})-W(\d{2})$/);
      prevWeekYear = prevMatch ? parseInt(prevMatch[1], 10) : null;
    }

    const showYearBadge = weekYear && (
      (globalIndex === 0) // First week overall
      || (prevWeekYear !== null && weekYear !== prevWeekYear) // Year changed
    );

    previousYear = weekYear;

    // Build blocks HTML
    let blocksHtml = '';
    blocks.forEach((block) => {
      const styles = getBlockStyles(block.colorId);
      blocksHtml += `
        <span style="
          display: inline-block;
          padding: 3px 8px;
          margin: 2px 3px 2px 0;
          border-radius: 4px;
          font-size: 10px;
          font-weight: 500;
          background: ${styles.background};
          border: 1px solid ${styles.border};
          color: ${styles.text};
          white-space: nowrap;
        ">${escapeHtml(block.name)}</span>
      `;
    });

    // Year badge HTML
    const yearBadgeHtml = showYearBadge
      ? `<div style="font-size: 9px; color: #fff; background: #424242; padding: 2px 6px; border-radius: 3px; margin-top: 3px; display: inline-block;">${weekYear}</div>`
      : '';

    html += `
      <div style="display: flex; min-height: 44px; border-bottom: 1px solid #e8e8e8; background: #ffffff;" data-week="${week}">
        <div style="width: 130px; padding: 8px 10px; display: flex; flex-direction: column; justify-content: center; border-right: 1px solid #e8e8e8;">
          <div style="display: flex; align-items: center; gap: 6px;">
            <span style="font-weight: 600; font-size: 13px; color: #333;">W${weekNumber}</span>
            ${yearBadgeHtml}
          </div>
          <div style="font-size: 10px; color: #666; margin-top: 2px;">${dateRange}</div>
        </div>
        <div style="flex: 1; padding: 6px 10px; display: flex; flex-wrap: wrap; align-items: center; align-content: center; border-right: 1px solid #e8e8e8;">
          ${blocksHtml || '<span style="color: #bbb;">—</span>'}
        </div>
        <div style="width: 260px; padding: 6px 10px; font-size: 10px; line-height: 1.4; color: #555; display: flex; align-items: flex-start; white-space: pre-wrap;">${escapeHtml(noteText) || '<span style="color: #bbb;">—</span>'}</div>
      </div>
    `;
  });

  container.innerHTML = html;
  return container;
};

/**
 * Capture the export container as a canvas
 */
const captureContainer = async (container) => {
  const { canvasScale } = PDF_CONFIG;

  const canvas = await html2canvas(container, {
    scale: canvasScale,
    useCORS: true,
    allowTaint: false, // Must be false to avoid canvas tainting with cross-origin images
    logging: false,
    backgroundColor: '#ffffff',
  });

  return canvas;
};

/**
 * Split weeks into pages based on row count
 */
const splitWeeksIntoPages = (weeks, rowsPerFirstPage, rowsPerPage) => {
  const pages = [];
  let currentIndex = 0;

  // First page has fewer rows due to header
  const firstPageRows = Math.min(rowsPerFirstPage, weeks.length);
  if (firstPageRows > 0) {
    pages.push({
      weeks: weeks.slice(0, firstPageRows),
      isFirstPage: true,
      startIndex: 0,
    });
    currentIndex = firstPageRows;
  }

  // Remaining pages
  while (currentIndex < weeks.length) {
    const endIndex = Math.min(currentIndex + rowsPerPage, weeks.length);
    pages.push({
      weeks: weeks.slice(currentIndex, endIndex),
      isFirstPage: false,
      startIndex: currentIndex,
    });
    currentIndex = endIndex;
  }

  return pages;
};

/**
 * Main export function
 */
export const generatePlannerPDF = async ({
  fromWeek,
  toWeek,
  calendarName,
  orgLogo,
  getState,
  onProgress,
}) => {
  try {
    onProgress?.('Fetching data...');

    // Get account code and plan ID from Redux state
    const state = getState();
    const accountCode = state?.auth?.data?.currentUser?.accountCode;
    const planId = state?.planner?.data?.planId;

    if (!accountCode) {
      throw new Error('No account selected');
    }
    if (!planId) {
      throw new Error('No plan selected');
    }

    onProgress?.('Loading images...');
    const orgLogoUrl = getOrgLogoDirectUrl(orgLogo);

    const teamBuildrLogoBase64 = await preloadImage(TeamBuildrLogo);

    onProgress?.('Fetching plan data...');
    const apiData = await fetchExportData({
      accountCode,
      planId,
      fromWeek,
      toWeek,
    });

    // Process data into week structure
    const { weeks, blocksByWeek, notesByWeek } = processDataForExport(
      apiData,
      fromWeek,
      toWeek,
    );

    // Split weeks into pages - more rows per page to reduce empty space
    // First page: 18 rows (header takes some space), other pages: 20 rows
    const pages = splitWeeksIntoPages(weeks, 18, 20);

    // Create PDF
    const {
      pageWidth,
      marginTop,
      marginLeft,
      marginRight,
    } = PDF_CONFIG;

    const contentWidth = pageWidth - marginLeft - marginRight;

    // eslint-disable-next-line new-cap
    const pdf = new jsPDF('p', 'mm', 'a4');

    // Generate each page
    for (let pageIndex = 0; pageIndex < pages.length; pageIndex += 1) {
      const page = pages[pageIndex];
      onProgress?.(`Generating page ${pageIndex + 1}/${pages.length}...`);

      // Create container for this page
      const container = createExportContainer({
        weeks: page.weeks,
        blocksByWeek,
        notesByWeek,
        calendarName,
        orgLogoUrl,
        teamBuildrLogoBase64,
        isFirstPage: page.isFirstPage,
        allWeeks: weeks,
        globalWeekStartIndex: page.startIndex,
      });

      let canvas;
      try {
        // Add to DOM temporarily
        document.body.appendChild(container);

        // eslint-disable-next-line no-await-in-loop, no-promise-executor-return
        await new Promise((r) => setTimeout(r, 50));

        // Capture as canvas
        // eslint-disable-next-line no-await-in-loop
        canvas = await captureContainer(container);
      } finally {
        // Always remove from DOM, even if an error occurred
        if (container.parentNode) {
          container.parentNode.removeChild(container);
        }
      }

      // Add page to PDF
      if (pageIndex > 0) {
        pdf.addPage();
      }

      const imgWidth = contentWidth;
      const imgHeight = (canvas.height / canvas.width) * imgWidth;

      const imgData = canvas.toDataURL('image/png', 0.92);
      pdf.addImage(imgData, 'PNG', marginLeft, marginTop, imgWidth, imgHeight);
    }

    // Generate filename and save
    const safeCalendarName = calendarName
      ? calendarName.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()
      : 'training-plan';
    const filename = `${safeCalendarName}-${fromWeek}-to-${toWeek}.pdf`;

    pdf.save(filename);
    onProgress?.(null);

    // eslint-disable-next-line no-console
    console.log('[pdfExportService] PDF saved:', filename);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('[pdfExportService] Export failed:', error);
    throw error;
  }
};

export default generatePlannerPDF;
